import z from "zod";

import { FileSchema } from "@/schemas/file";
import { candidateFileIdSchema } from "@/types/candidateFile";
import { questionIdSchema, videoLinkUrlSchema } from "@/types/question";

export const AnswerType = {
  OPEN_RESPONSE: "open_response",
  YES_NO: "yes_no",
  SINGLE_CHOICE: "single_choice",
  MULTIPLE_CHOICE: "multiple_choice",
  STATEMENT_CHECKBOX: "statement_checkbox",
  ATTACHMENT: "attachment",
  VIDEO_LINK: "video_link",
  DATE: "date",
} as const;
export type AnswerType = (typeof AnswerType)[keyof typeof AnswerType];
export const AnswerTypeSchema = z.enum(AnswerType);

export const AnswerScope = {
  GLOBAL: "global",
  TENANT: "tenant",
  JOB: "job",
} as const;
export type AnswerScope = (typeof AnswerScope)[keyof typeof AnswerScope];
export const AnswerScopeSchema = z.enum(AnswerScope);

export const ChoiceListSchema = z.array(z.string()).min(1);

export const QuestionDependencySchema = z.object({
  questionId: questionIdSchema,
  option: z.string(),
});
export type QuestionDependency = z.infer<typeof QuestionDependencySchema>;

const QuestionShape = {
  id: questionIdSchema,
  question: z.string(),
  description: z.string().nullable(),
  required: z.boolean(),
  scope: AnswerScopeSchema,
  dependsOn: QuestionDependencySchema.nullable(),
};

export const QuestionAndAnswerSchema = z.union([
  z.object({
    question: z.object({
      ...QuestionShape,
      type: z.literal(AnswerType.OPEN_RESPONSE),
    }),
    answer: z.string().nullable(),
  }),
  z.object({
    question: z.object({
      ...QuestionShape,
      type: z.literal(AnswerType.YES_NO),
    }),
    answer: z.boolean().nullable(),
  }),
  z.object({
    question: z.object({
      ...QuestionShape,
      type: z.literal(AnswerType.STATEMENT_CHECKBOX),
    }),
    answer: z.boolean().nullable(),
  }),
  z.object({
    question: z.object({
      ...QuestionShape,
      type: z.literal(AnswerType.SINGLE_CHOICE),
      options: ChoiceListSchema,
    }),
    answer: z.string().nullable(),
  }),
  z.object({
    question: z.object({
      ...QuestionShape,
      type: z.literal(AnswerType.MULTIPLE_CHOICE),
      options: ChoiceListSchema,
    }),
    answer: ChoiceListSchema.nullable(),
  }),
  z.object({
    question: z.object({
      ...QuestionShape,
      type: z.literal(AnswerType.ATTACHMENT),
    }),
    answer: FileSchema.nullable(),
  }),
  z.object({
    question: z.object({
      ...QuestionShape,
      type: z.literal(AnswerType.VIDEO_LINK),
    }),
    answer: videoLinkUrlSchema.nullable(),
  }),
  z.object({
    question: z.object({
      ...QuestionShape,
      type: z.literal(AnswerType.DATE),
    }),
    answer: z.iso.date().nullable(),
  }),
]);
export type QuestionAndAnswer = z.infer<typeof QuestionAndAnswerSchema>;

export const AnswerCreateSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(AnswerType.OPEN_RESPONSE),
    questionId: questionIdSchema,
    scope: AnswerScopeSchema,
    answer: z.string().nullable(),
  }),
  z.object({
    type: z.literal(AnswerType.YES_NO),
    questionId: questionIdSchema,
    scope: AnswerScopeSchema,
    answer: z.boolean().nullable(),
  }),
  z.object({
    type: z.literal(AnswerType.STATEMENT_CHECKBOX),
    questionId: questionIdSchema,
    scope: AnswerScopeSchema,
    answer: z.boolean().nullable(),
  }),
  z.object({
    type: z.literal(AnswerType.SINGLE_CHOICE),
    questionId: questionIdSchema,
    scope: AnswerScopeSchema,
    answer: z.string().nullable(),
  }),
  z.object({
    type: z.literal(AnswerType.MULTIPLE_CHOICE),
    questionId: questionIdSchema,
    scope: AnswerScopeSchema,
    answer: ChoiceListSchema.nullable(),
  }),
  z.object({
    type: z.literal(AnswerType.ATTACHMENT),
    questionId: questionIdSchema,
    scope: AnswerScopeSchema,
    answer: candidateFileIdSchema.nullable(),
  }),
  z.object({
    type: z.literal(AnswerType.VIDEO_LINK),
    questionId: questionIdSchema,
    scope: AnswerScopeSchema,
    answer: videoLinkUrlSchema.nullable(),
  }),
  z.object({
    type: z.literal(AnswerType.DATE),
    questionId: questionIdSchema,
    scope: AnswerScopeSchema,
    answer: z.iso.date().nullable(),
  }),
]);
export type AnswerCreate = z.infer<typeof AnswerCreateSchema>;
